import React from "react";
import PropTypes from "prop-types";
import { FormField, Select } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import SchedulePicker from "./SchedulePicker";
import { TRIGGER_TYPES, getTriggerMeta } from "./automationUtils";

/**
 * The editor's Trigger step: choose HOW the automation fires —
 *   • Routine schedule — the scheduler scans everyone on given days/hour (UTC)
 *     and fires once per user when they enter the situation;
 *   • Event — fires immediately, every time a code-defined event happens to a
 *     user (website created, subscription cancelled, …); no situation.
 * Renders the schedule picker or the event picker underneath the choice.
 */
const TriggerPicker = ({
  triggerType = "routine",
  triggerEvent = "",
  days = [],
  hour = "9",
  events = [],
  onTypeChange,
  onEventChange,
  onScheduleChange,
  disabled = false,
  errors = {},
}) => {
  const selectedEvent = events.find((e) => e.key === triggerEvent) || null;
  const eventOptions = [
    { value: "", label: "Choose an event…", disabled: true },
    ...events.map((e) => ({ value: e.key, label: e.label })),
  ];

  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Trigger type" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TRIGGER_TYPES.map((type) => {
          const meta = getTriggerMeta(type);
          const selected = triggerType === type;
          return (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => !selected && onTypeChange?.(type)}
              className={[
                "text-left rounded-2xl border p-3 sm:p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
                selected
                  ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                  : `${borders.default} ${surfaces.primary} ${surfaces.hover}`,
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                    selected ? "border-blue-600 dark:border-blue-400" : "border-gray-400 dark:border-dark-500"
                  }`}
                >
                  {selected ? <span className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" /> : null}
                </span>
                <span className="min-w-0">
                  <span className={`block ${typography.bodySmall} font-semibold ${textColors.primary}`}>{meta.label}</span>
                  <span className={`block mt-0.5 ${typography.caption} ${textColors.tertiary}`}>{meta.description}</span>
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {triggerType === "event" ? (
        <div className="space-y-3">
          <FormField
            label="Event"
            htmlFor="automation-trigger-event"
            required
            helperText="The automation fires for the user the event happens to — every time it happens."
          >
            <Select
              id="automation-trigger-event"
              value={triggerEvent || ""}
              onChange={(e) => onEventChange?.(e.target.value)}
              options={eventOptions}
              placeholder={null}
              disabled={disabled || events.length === 0}
              error={triggerEvent ? errors?.trigger_event || "" : ""}
            />
          </FormField>
          {selectedEvent ? (
            <p className={`${typography.caption} ${textColors.secondary}`} aria-live="polite">
              {selectedEvent.description}
            </p>
          ) : null}
          <p className={`${typography.caption} ${textColors.tertiary}`}>
            No situation is evaluated for event automations — use guards to narrow who receives the messages. Each
            occurrence counts once: the same event can never send the same message twice to a user.
          </p>
        </div>
      ) : (
        <SchedulePicker days={days} hour={hour} onChange={onScheduleChange} disabled={disabled} errors={errors} />
      )}
    </div>
  );
};

TriggerPicker.propTypes = {
  triggerType: PropTypes.oneOf(["routine", "event"]),
  triggerEvent: PropTypes.string,
  days: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  hour: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  events: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, description: PropTypes.string })),
  onTypeChange: PropTypes.func,
  onEventChange: PropTypes.func,
  onScheduleChange: PropTypes.func,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default TriggerPicker;
